/**
 * Presentation helpers for the ad attribution stored on bookings and quotes.
 *
 * Pure, so the formatting is unit-testable and the booking page and quote page
 * can't drift into describing the same landing URL differently.
 */

import { extractAdParams, resolveClickId, type AdParams } from '@limo/shared'

export interface AttributionFields {
  landing_url?: string | null
  gclid?: string | null
  utm_source?: string | null
  utm_medium?: string | null
  utm_campaign?: string | null
  utm_term?: string | null
  utm_content?: string | null
}

/** True when there is anything at all worth rendering. */
export function hasAttribution(row: AttributionFields | null | undefined): boolean {
  if (!row) return false
  return Boolean(
    row.landing_url ||
      row.gclid ||
      row.utm_source ||
      row.utm_medium ||
      row.utm_campaign ||
      row.utm_term ||
      row.utm_content
  )
}

export interface LandingUrlSummary {
  /** Host + path only — the readable part. */
  readonly path: string
  /** How many query/fragment parameters the URL carried. */
  readonly paramCount: number
  /** Short form for the collapsed view, e.g. "/limo-service · 5 params". */
  readonly summary: string
  /** The untouched original, for the expanded view and the copy button. */
  readonly full: string
}

/**
 * Condense a landing URL for display.
 *
 * Ad tracking templates routinely produce 200+ character URLs; rendering one
 * raw would swamp the card. The full string is always kept for copy/expand —
 * it's the source of truth for every click ID we deliberately didn't columnise.
 */
export function summarizeLandingUrl(landingUrl: string | null | undefined): LandingUrlSummary | null {
  if (!landingUrl) return null

  const full = landingUrl
  let path = landingUrl
  let paramCount = 0

  try {
    const parsed = new URL(landingUrl)
    path = `${parsed.host}${parsed.pathname === '/' ? '' : parsed.pathname}`

    // Count params across the query AND the fragment, because Google's tracking
    // templates sometimes append them after a '#'.
    //
    // Only key=value pairs count. A bare fragment label ("#rates") parses as a
    // valueless key, and counting it would report "3 params" for a URL carrying
    // two — the same non-empty-value rule extractAdParams applies.
    const segments = landingUrl.split(/[?#]/).slice(1)
    for (const segment of segments) {
      for (const [, value] of new URLSearchParams(segment).entries()) {
        if (value.trim()) paramCount++
      }
    }
  } catch {
    // Not a parseable URL — show it as-is rather than hiding it.
    path = landingUrl
  }

  const summary =
    paramCount > 0 ? `${path} · ${paramCount} param${paramCount === 1 ? '' : 's'}` : path

  return { path, paramCount, summary, full }
}

export interface ClickIdentifier {
  readonly type: 'gclid' | 'gbraid' | 'wbraid'
  readonly value: string
  /** Why this identifier exists, in operator language. */
  readonly note: string
}

const CLICK_ID_NOTES: Record<ClickIdentifier['type'], string> = {
  gclid: 'Standard Google Ads click ID',
  gbraid: 'iOS app click — Google Ads uses this when gclid is unavailable',
  wbraid: 'iOS web click — Google Ads uses this when gclid is unavailable',
}

/**
 * The Google click identifier for this row.
 *
 * Prefers the stored `gclid` column, then falls back to parsing the landing URL,
 * which is where gbraid and wbraid live — they have no column of their own by
 * design, since the URL always carries them.
 */
export function resolveStoredClickId(
  row: AttributionFields | null | undefined
): ClickIdentifier | null {
  if (!row) return null

  const fromUrl: AdParams = extractAdParams(row.landing_url ?? '')
  const merged: AdParams = { ...fromUrl, ...(row.gclid ? { gclid: row.gclid } : {}) }
  const resolved = resolveClickId(merged)
  if (!resolved) return null

  return { type: resolved.type, value: resolved.value, note: CLICK_ID_NOTES[resolved.type] }
}

/** Every non-Google parameter worth showing, parsed out of the landing URL. */
export function otherPlatformClickIds(
  landingUrl: string | null | undefined
): { readonly label: string; readonly value: string }[] {
  const params = extractAdParams(landingUrl ?? '')
  const out: { label: string; value: string }[] = []
  if (params.msclkid) out.push({ label: 'Microsoft (msclkid)', value: params.msclkid })
  if (params.fbclid) out.push({ label: 'Meta (fbclid)', value: params.fbclid })
  return out
}

/** UTM tags as label/value pairs, omitting the empty ones. */
export function utmPairs(
  row: AttributionFields | null | undefined
): { readonly label: string; readonly value: string }[] {
  if (!row) return []
  const candidates: [string, string | null | undefined][] = [
    ['Source', row.utm_source],
    ['Medium', row.utm_medium],
    ['Campaign', row.utm_campaign],
    ['Term', row.utm_term],
    ['Content', row.utm_content],
  ]
  return candidates
    .filter(([, value]) => Boolean(value))
    .map(([label, value]) => ({ label, value: value as string }))
}

/**
 * A one-line answer to "where did this booking come from?".
 *
 * Paid search is asserted only when a Google click ID is present — UTM tags
 * alone can be set by anyone, including on organic links.
 */
export function describeTrafficSource(row: AttributionFields | null | undefined): string {
  if (!hasAttribution(row)) return 'No attribution recorded'

  const clickId = resolveStoredClickId(row)
  if (clickId) {
    const campaign = row?.utm_campaign ? ` · ${row.utm_campaign}` : ''
    return `Paid Google Ads click${campaign}`
  }

  const source = row?.utm_source
  const medium = row?.utm_medium
  if (source) return medium ? `${source} / ${medium}` : source

  return 'Tagged visit, no click ID'
}
